import { useCallback, useEffect, useState } from "react";
import { CircleCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { Kpi, PageHead } from "@/components/Bits";
import { ExportButtons } from "@/components/ExportButtons";
import { usePaged, ShowMore } from "@/lib/usePaged";
import { can } from "@/lib/rbac";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip as ReTooltip, Legend,
} from "recharts";

const TABS = [
  ["summary", "Financial summary"],
  ["billing", "Studio billing"],
  ["expenses", "Expenses"],
  ["analysis", "Expense analysis"],
  ["installers", "Installer payments"],
  ["salaries", "Salary register"],
];

const STATUS_TINT = {
  paid: "bg-emerald-50 text-emerald-700",
  unpaid: "bg-amber-50 text-amber-700",
  overdue: "bg-crimson-50 text-crimson-700",
  processed: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
};

const INVOICE_COLUMNS = [
  { label: "Invoice", value: (r) => r.invoice_no },
  { label: "Studio", value: (r) => r.studio_name },
  { label: "Base", value: (r) => r.base_amount },
  { label: "GST", value: (r) => r.gst_total },
  { label: "Payable", value: (r) => r.amount },
  { label: "HQ margin", value: (r) => r.hq_margin },
  { label: "Raised", value: (r) => r.created_at },
  { label: "Due", value: (r) => r.due_date },
  { label: "Status", value: (r) => r.payment_status },
  { label: "Reference", value: (r) => r.payment_reference },
];

const CHART_COLORS = ["#3F0A72", "#F5185B", "#2563eb", "#0d9488", "#ea580c", "#7c3aed", "#ca8a04", "#0891b2"];

function ExpensesTab({ canWrite }) {
  const [heads, setHeads] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState({ head: "", amount: "", date: new Date().toISOString().slice(0, 10), notes: "" });
  const [newHead, setNewHead] = useState("");
  const load = useCallback(() => {
    api.get("/expense-heads").then(({ data }) => setHeads(data)).catch(() => setHeads([]));
    api.get("/expenses").then(({ data }) => { setRows(data.rows); setTotal(data.total); }).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addHead = async () => {
    if (!newHead.trim()) return;
    try { await api.post("/expense-heads", { name: newHead.trim() }); setNewHead(""); load(); toast.success("Head added"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addExpense = async () => {
    if (!form.head) return toast.error("Select an expense head");
    if (!(Number(form.amount) > 0)) return toast.error("Enter an amount");
    try {
      await api.post("/expenses", { head: form.head, amount: Number(form.amount), date: form.date, notes: form.notes });
      setForm((f) => ({ ...f, amount: "", notes: "" })); load(); toast.success("Expense logged");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const removeExpense = async (id) => {
    try { await api.delete(`/expenses/${id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="expenses-tab">
      {canWrite && (
        <Card className="border-brand-line shadow-none">
          <CardHeader><CardTitle className="text-lg">Log an expense</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs">Add a custom expense head</Label>
                <div className="mt-1 flex gap-2">
                  <Input data-testid="new-head-input" placeholder="e.g. Rent, Salaries, Logistics"
                    value={newHead} onChange={(e) => setNewHead(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addHead()} />
                  <Button variant="outline" data-testid="add-head-btn" onClick={addHead}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div><Label className="text-xs">Expense head</Label>
                <Select value={form.head} onValueChange={(v) => setForm((f) => ({ ...f, head: v }))}>
                  <SelectTrigger className="mt-1 min-h-[44px]" data-testid="expense-head-select"><SelectValue placeholder="Select head" /></SelectTrigger>
                  <SelectContent>{heads.map((h) => <SelectItem key={h.id} value={h.name}>{h.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Amount (₹)</Label>
                <Input className="mt-1 min-h-[44px]" inputMode="decimal" data-testid="expense-amount"
                  value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} /></div>
              <div><Label className="text-xs">Date</Label>
                <Input className="mt-1 min-h-[44px]" type="date" value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
              <div><Label className="text-xs">Notes (optional)</Label>
                <Input className="mt-1 min-h-[44px]" value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <Button data-testid="add-expense-btn" onClick={addExpense}>Log expense</Button>
          </CardContent>
        </Card>
      )}
      <Card className="border-brand-line shadow-none">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-lg">Expense ledger</CardTitle>
          <span className="text-sm font-semibold text-brand-ink" data-testid="expense-ledger-total">Total: {money(total)}</span>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="mobile-cards">
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Head</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Notes</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="p-8 text-center text-slate-500">No expenses logged yet.</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`expense-row-${r.id}`}>
                  <TableCell data-label="Date">{r.date}</TableCell>
                  <TableCell data-label="Head" className="font-medium">{r.head}</TableCell>
                  <TableCell data-label="Amount" className="text-right">{money(r.amount)}</TableCell>
                  <TableCell data-label="Notes" className="text-slate-500">{r.notes || "—"}</TableCell>
                  <TableCell>{canWrite && <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => removeExpense(r.id)}><Trash2 className="h-4 w-4" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ExpenseAnalysis() {
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/expenses/analysis", { params: { start: range.start || undefined, end: range.end || undefined } })
      .then(({ data }) => setData(data)).catch(() => setData(null));
  }, [range]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="expense-analysis">
      <Card className="border-brand-line shadow-none">
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div><Label className="text-xs">From</Label>
            <Input className="mt-1" type="date" data-testid="analysis-start" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} /></div>
          <div><Label className="text-xs">To</Label>
            <Input className="mt-1" type="date" data-testid="analysis-end" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} /></div>
          <div className="ml-auto text-right">
            <p className="text-xs text-slate-500">Total for range</p>
            <p className="font-display text-2xl font-semibold text-brand-ink" data-testid="analysis-total">{money(data.total)}</p>
          </div>
        </CardContent>
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-brand-line shadow-none">
          <CardHeader><CardTitle className="text-lg">Spend by head</CardTitle></CardHeader>
          <CardContent style={{ height: 300 }}>
            {data.by_head.length === 0 ? <p className="text-sm text-slate-500">No data.</p> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.by_head} dataKey="amount" nameKey="head" cx="50%" cy="50%" outerRadius={100} label={(e) => e.head}>
                    {data.by_head.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <ReTooltip formatter={(v) => money(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card className="border-brand-line shadow-none">
          <CardHeader><CardTitle className="text-lg">Month-on-month trend</CardTitle></CardHeader>
          <CardContent style={{ height: 300 }}>
            {data.trend.length === 0 ? <p className="text-sm text-slate-500">No data.</p> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} />
                  <ReTooltip formatter={(v) => money(v)} /><Legend />
                  {data.heads.map((h, i) => <Bar key={h} dataKey={h} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function Accounting() {
  const { user } = useAuth();
  const canWrite = can(user, "finance:write");
  const canStaff = can(user, "users:write");
  const [tab, setTab] = useState("summary");
  const [month, setMonth] = useState("");
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState({ rows: [], totals: {} });
  const [installers, setInstallers] = useState({ rows: [], totals: {} });
  const [salaries, setSalaries] = useState({ rows: [], totals: {} });
  const [payFor, setPayFor] = useState(null);
  const [reference, setReference] = useState("");
  const [staffDraft, setStaffDraft] = useState(null);
  const [rateFor, setRateFor] = useState(null);
  const [rate, setRate] = useState("");

  const load = useCallback(() => {
    const params = month ? { month } : {};
    api.get("/finance/summary", { params }).then(({ data }) => {
      setSummary(data);
      if (!month) setMonth(data.month);
    });
    api.get("/finance/invoices").then(({ data }) => setInvoices(data));
    api.get("/finance/installer-payments", { params }).then(({ data }) => setInstallers(data));
    api.get("/finance/salaries", { params }).then(({ data }) => setSalaries(data));
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const invoicePaged = usePaged(invoices.rows);
  const installerPaged = usePaged(installers.rows);
  const salaryPaged = usePaged(salaries.rows);

  const markPaid = async () => {
    try {
      await api.post(`/finance/invoices/${payFor.id}/pay`, { payment_reference: reference });
      toast.success(`${payFor.invoice_no} marked paid`);
      setPayFor(null);
      setReference("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const processInstaller = async (r) => {
    try {
      await api.post(`/finance/installer-payments/${r.installer_id}/process`, { month });
      toast.success(`${r.name} payment processed for ${month}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const paySalary = async (r) => {
    try {
      await api.post(`/finance/salaries/${r.id}/pay`, { month });
      toast.success(`${r.name} salary marked paid`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveStaff = async () => {
    try {
      await api.post("/finance/salaries", {
        name: staffDraft.name,
        designation: staffDraft.designation,
        monthly_salary: Number(staffDraft.monthly_salary || 0),
      });
      toast.success("Staff member added");
      setStaffDraft(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveRate = async () => {
    try {
      await api.put(`/teams/${rateFor.installer_id}/rate`, { rate_per_job: Number(rate || 0) });
      toast.success(`Rate updated for ${rateFor.name}`);
      setRateFor(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const summaryRows = summary
    ? [
        { item: "Studio billings invoiced", amount: summary.billing.invoiced },
        { item: "Collected", amount: summary.billing.collected },
        { item: "Outstanding", amount: summary.billing.outstanding },
        { item: "Overdue", amount: summary.billing.overdue },
        { item: "HQ material margin", amount: summary.billing.hq_margin },
        { item: "Installer payments due", amount: summary.installers.payable },
        { item: "Installer payments paid", amount: summary.installers.paid },
        { item: "Salary payroll", amount: summary.salaries.payroll },
        { item: "Salaries paid", amount: summary.salaries.paid },
        { item: "Total HQ outgo", amount: summary.outgo },
      ]
    : [];

  return (
    <div>
      <PageHead title="HQ Accounts" subtitle="Studio billing, installer payments and payroll — HQ numbers only">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="min-h-[44px] w-[150px]" data-testid="finance-month-select">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {(summary?.months || []).map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ExportButtons
            filename={`veloskin-hq-finance-${month}`}
            title={`HQ financial summary ${month}`}
            columns={[
              { label: "Item", value: (r) => r.item },
              { label: "Amount", value: (r) => r.amount },
            ]}
            rows={summaryRows}
            testidPrefix="finance"
          />
        </div>
      </PageHead>

      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            data-testid={`finance-tab-${k}`}
            onClick={() => setTab(k)}
            className={`min-h-[44px] rounded-full px-4 text-sm font-medium transition-colors duration-200 ${
              tab === k ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-brand-tint"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "expenses" && <ExpensesTab canWrite={canWrite} />}
      {tab === "analysis" && <ExpenseAnalysis />}

      {tab === "summary" && summary && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Invoiced" value={money(summary.billing.invoiced)} testid="kpi-invoiced" />
            <Kpi
              label="Collected"
              value={money(summary.billing.collected)}
              accent="text-emerald-600"
              testid="kpi-collected"
            />
            <Kpi
              label="Outstanding"
              value={money(summary.billing.outstanding)}
              accent="text-crimson-600"
              testid="kpi-outstanding"
            />
            <Kpi label="HQ outgo" value={money(summary.outgo)} testid="kpi-outgo" />
          </div>
          <Card className="border-brand-line shadow-none">
            <CardHeader>
              <CardTitle className="text-base">{month} — HQ position</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table data-testid="finance-summary-table" className="mobile-cards">
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summaryRows.map((r) => (
                    <TableRow key={r.item} className="hover:bg-brand-tint/60">
                      <TableCell data-label="Item" className="font-medium">
                        {r.item}
                      </TableCell>
                      <TableCell data-label="Amount" className="text-right">
                        {money(r.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {tab === "billing" && (
        <Card className="border-brand-line shadow-none">
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-base">
              Studio billing · {money(invoices.totals.outstanding)} outstanding
            </CardTitle>
            <ExportButtons
              filename="veloskin-studio-billing"
              title="Studio billing"
              columns={INVOICE_COLUMNS}
              rows={invoices.rows}
              testidPrefix="billing"
            />
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table data-testid="invoices-table" className="mobile-cards">
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Studio</TableHead>
                  <TableHead className="text-right">Base</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Payable</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoicePaged.visible.map((r) => (
                  <TableRow key={r.id} className="hover:bg-brand-tint/60" data-testid={`invoice-row-${r.id}`}>
                    <TableCell data-label="Invoice" className="font-mono text-xs font-semibold">
                      {r.invoice_no}
                    </TableCell>
                    <TableCell data-label="Studio">{r.studio_name}</TableCell>
                    <TableCell data-label="Base" className="text-right text-slate-600">
                      {money(r.base_amount)}
                    </TableCell>
                    <TableCell data-label="GST" className="text-right text-slate-600" data-testid={`invoice-gst-${r.id}`}>
                      {money(r.gst_total)}
                    </TableCell>
                    <TableCell data-label="Payable" className="text-right font-semibold">
                      {money(r.amount)}
                    </TableCell>
                    <TableCell data-label="Due" className="text-slate-500">
                      {r.due_date}
                    </TableCell>
                    <TableCell data-label="Status">
                      <Badge
                        data-testid={`invoice-status-${r.id}`}
                        className={`border-0 capitalize ${STATUS_TINT[r.payment_status]}`}
                      >
                        {r.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell data-label="Reference" className="text-xs text-slate-500">
                      {r.payment_reference || "—"}
                    </TableCell>
                    <TableCell data-label="" className="text-right">
                      {canWrite && r.payment_status !== "paid" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="min-h-[44px]"
                          data-testid={`mark-paid-${r.id}`}
                          onClick={() => {
                            setPayFor(r);
                            setReference("");
                          }}
                        >
                          Mark paid
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ShowMore paged={invoicePaged} testid="invoices-show-more" />
          </CardContent>
        </Card>
      )}

      {tab === "installers" && (
        <Card className="border-brand-line shadow-none">
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-base">
              Installer payments · {month} · {money(installers.totals.payable)} payable
            </CardTitle>
            <ExportButtons
              filename={`veloskin-installer-payments-${month}`}
              title={`Installer payments ${month}`}
              columns={[
                { label: "Team", value: (r) => r.name },
                { label: "Leader", value: (r) => r.leader_name },
                { label: "Studio", value: (r) => r.studio_name },
                { label: "Jobs", value: (r) => r.jobs_completed },
                { label: "Rate", value: (r) => r.rate_per_job },
                { label: "Payable", value: (r) => r.total_payable },
                { label: "Status", value: (r) => r.status },
              ]}
              rows={installers.rows}
              testidPrefix="installer-pay"
            />
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table data-testid="installer-payments-table" className="mobile-cards">
              <TableHeader>
                <TableRow>
                  <TableHead>Team</TableHead>
                  <TableHead>Studio</TableHead>
                  <TableHead className="text-right">Jobs</TableHead>
                  <TableHead className="text-right">Rate/job</TableHead>
                  <TableHead className="text-right">Payable</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {installerPaged.visible.map((r) => (
                  <TableRow
                    key={r.installer_id}
                    className="hover:bg-brand-tint/60"
                    data-testid={`installer-pay-row-${r.installer_id}`}
                  >
                    <TableCell data-label="Team" className="font-medium">
                      {r.name}
                      <span className="block text-xs text-slate-500">{r.leader_name}</span>
                    </TableCell>
                    <TableCell data-label="Studio" className="text-slate-500">
                      {r.studio_name}
                    </TableCell>
                    <TableCell data-label="Jobs" className="text-right">
                      {r.jobs_completed}
                    </TableCell>
                    <TableCell data-label="Rate/job" className="text-right">
                      {canWrite ? (
                        <button
                          data-testid={`set-rate-${r.installer_id}`}
                          onClick={() => {
                            setRateFor(r);
                            setRate(String(r.rate_per_job || ""));
                          }}
                          className="underline-offset-2 hover:underline"
                        >
                          {money(r.rate_per_job)}
                        </button>
                      ) : (
                        money(r.rate_per_job)
                      )}
                    </TableCell>
                    <TableCell data-label="Payable" className="text-right font-semibold">
                      {money(r.total_payable)}
                    </TableCell>
                    <TableCell data-label="Status">
                      <Badge className={`border-0 capitalize ${STATUS_TINT[r.status]}`}>
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell data-label="" className="text-right">
                      {canWrite && r.status !== "processed" && r.total_payable > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="min-h-[44px]"
                          data-testid={`process-payment-${r.installer_id}`}
                          onClick={() => processInstaller(r)}
                        >
                          Mark processed
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ShowMore paged={installerPaged} testid="installer-pay-show-more" />
          </CardContent>
        </Card>
      )}

      {tab === "salaries" && (
        <Card className="border-brand-line shadow-none">
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-base">
              Salary register · {month} · {money(salaries.totals.payroll)} payroll
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              {canStaff && (
                <Button
                  size="sm"
                  className="min-h-[44px]"
                  data-testid="add-staff-btn"
                  onClick={() => setStaffDraft({ name: "", designation: "", monthly_salary: "" })}
                >
                  <Plus className="mr-2 h-4 w-4" /> Add HQ staff
                </Button>
              )}
              <ExportButtons
                filename={`veloskin-salaries-${month}`}
                title={`Salary register ${month}`}
                columns={[
                  { label: "Name", value: (r) => r.name },
                  { label: "Designation", value: (r) => r.designation },
                  { label: "Monthly salary", value: (r) => r.monthly_salary },
                  { label: "Status", value: (r) => r.status },
                  { label: "Paid on", value: (r) => r.paid_on },
                ]}
                rows={salaries.rows}
                testidPrefix="salary"
              />
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table data-testid="salaries-table" className="mobile-cards">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead className="text-right">Monthly salary</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {salaries.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-slate-500">
                      No HQ staff added yet.
                    </TableCell>
                  </TableRow>
                )}
                {salaryPaged.visible.map((r) => (
                  <TableRow key={r.id} className="hover:bg-brand-tint/60" data-testid={`salary-row-${r.id}`}>
                    <TableCell data-label="Name" className="font-medium">
                      {r.name}
                    </TableCell>
                    <TableCell data-label="Designation" className="text-slate-500">
                      {r.designation}
                    </TableCell>
                    <TableCell data-label="Monthly salary" className="text-right font-semibold">
                      {money(r.monthly_salary)}
                    </TableCell>
                    <TableCell data-label="Status">
                      <Badge className={`border-0 capitalize ${STATUS_TINT[r.status]}`}>
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell data-label="" className="space-x-2 text-right">
                      {canWrite && r.status !== "paid" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="min-h-[44px]"
                          data-testid={`pay-salary-${r.id}`}
                          onClick={() => paySalary(r)}
                        >
                          <CircleCheck className="mr-1.5 h-3.5 w-3.5" /> Mark paid
                        </Button>
                      )}
                      {canStaff && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="min-h-[44px]"
                          data-testid={`delete-staff-${r.id}`}
                          onClick={async () => {
                            await api.delete(`/finance/salaries/${r.id}`);
                            toast.success("Staff member removed");
                            load();
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-crimson-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ShowMore paged={salaryPaged} testid="salaries-show-more" />
          </CardContent>
        </Card>
      )}

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark {payFor?.invoice_no} paid</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Payment reference *</Label>
            <Input
              className="mt-1.5 min-h-[44px]"
              data-testid="payment-reference-input"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="UTR / NEFT reference"
            />
          </div>
          <DialogFooter>
            <Button
              className="min-h-[44px]"
              data-testid="confirm-payment-btn"
              onClick={markPaid}
              disabled={!reference.trim()}
            >
              Confirm payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rateFor} onOpenChange={(o) => !o && setRateFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rate per job — {rateFor?.name}</DialogTitle>
          </DialogHeader>
          <Input
            className="min-h-[44px]"
            data-testid="rate-per-job-input"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            inputMode="numeric"
          />
          <DialogFooter>
            <Button className="min-h-[44px]" data-testid="save-rate-btn" onClick={saveRate}>
              Save rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!staffDraft} onOpenChange={(o) => !o && setStaffDraft(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add HQ staff</DialogTitle>
          </DialogHeader>
          {staffDraft && (
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  data-testid="staff-name-input"
                  value={staffDraft.name}
                  onChange={(e) => setStaffDraft((d) => ({ ...d, name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Designation *</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  data-testid="staff-designation-input"
                  value={staffDraft.designation}
                  onChange={(e) => setStaffDraft((d) => ({ ...d, designation: e.target.value }))}
                />
              </div>
              <div>
                <Label>Monthly salary (₹) *</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  inputMode="numeric"
                  data-testid="staff-salary-input"
                  value={staffDraft.monthly_salary}
                  onChange={(e) => setStaffDraft((d) => ({ ...d, monthly_salary: e.target.value }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              className="min-h-[44px]"
              data-testid="save-staff-btn"
              onClick={saveStaff}
              disabled={
                !staffDraft?.name || !staffDraft?.designation || !staffDraft?.monthly_salary
              }
            >
              Add staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
